import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Plus, Minus, X, Download, ExternalLink, Search, Loader2, FileSearch, Star } from 'lucide-react';
import { api, absoluteUrl } from '../../lib/api';
import { useTranslation } from '../../i18nContext';

function formatSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function highlight(text, query) {
  if (!query) return text;
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${safe})`, 'gi'));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-amber-100 text-amber-900 rounded px-0.5">{p}</mark>
      : <span key={i}>{p}</span>
  );
}

// Embeddable "Publicaciones / Periodicals" section — tabs, full-text search and
// the PDF preview modal. Rendered on the public page and as the
// `periodicals_list` CMS block.

// Turn an internal group key ("repositorio-e87419") into a clean shareable
// slug ("repositorio") by dropping the auto-generated 6-hex suffix.
const stripHex = (k) => (k || '').replace(/-[0-9a-f]{6}$/i, '');

export default function PeriodicalsSection() {
  const t = useTranslation();
  const locale = t.locale;
  const [catalog, setCatalog] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [group, setGroup] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get('/periodicals').then(({ data }) => setCatalog(data || []));
    api.get('/periodicals/groups').then(({ data }) => {
      const gs = data || [];
      setGroups(gs);
      // Resolve the ?tab= param against the exact key OR the clean slug.
      let initial = null;
      try {
        const p = new URLSearchParams(window.location.search).get('tab');
        if (p) {
          const exact = gs.find((g) => g.key === p);
          if (exact) initial = exact.key;
          else {
            const bySlug = gs.filter((g) => stripHex(g.key) === p);
            if (bySlug.length === 1) initial = bySlug[0].key;
          }
        }
      } catch (e) { /* noop */ }
      const chosen = initial || (gs[0] ? gs[0].key : 'flash');
      setGroup((cur) => cur || chosen);
      // Normalize an existing ?tab= to the clean slug (old long-key links
      // keep working but the address bar becomes tidy).
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get('tab')) {
          const base = stripHex(chosen);
          const collides = gs.filter((g) => stripHex(g.key) === base).length > 1;
          url.searchParams.set('tab', collides ? chosen : base);
          window.history.replaceState({}, '', url);
        }
      } catch (e) { /* noop */ }
    });
  }, []);

  // Switch tab AND reflect it in the URL with a clean slug so the link is
  // shareable and opens directly on the chosen tab.
  const selectGroup = useCallback((key) => {
    setGroup(key);
    try {
      const base = stripHex(key);
      const collides = (groups || []).filter((g) => stripHex(g.key) === base).length > 1;
      const url = new URL(window.location.href);
      url.searchParams.set('tab', collides ? key : base);
      window.history.replaceState({}, '', url);
    } catch (e) { /* noop */ }
  }, []);

  const runSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/periodicals/search', { params: { q: trimmed } });
      setResults(data || []);
    } finally { setSearching(false); }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => runSearch(query), 400);
    return () => clearTimeout(id);
  }, [query, runSearch]);

  function togglePeriod(sectionId, periodId) {
    setExpanded((prev) => {
      const key = `${sectionId}::${periodId}`;
      const current = prev[key];
      if (current === undefined) {
        const section = catalog.find((s) => s.id === sectionId);
        const isFirst = section && section.periods[0]?.id === periodId;
        return { ...prev, [key]: !isFirst };
      }
      return { ...prev, [key]: !current };
    });
  }
  function isPeriodOpen(sectionId, periodId) {
    const key = `${sectionId}::${periodId}`;
    const v = expanded[key];
    if (v !== undefined) return v;
    const section = catalog.find((s) => s.id === sectionId);
    return section && section.periods[0]?.id === periodId;
  }
  function openPreview(url, title, page) {
    const absolute = absoluteUrl(url);
    const u = page ? `${absolute}#page=${page}` : absolute;
    setPreview({ url: u, title });
  }

  return (
    <>
      <section className="py-10 sm:py-14 bg-gray-50 min-h-screen" data-testid="periodicals-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 sm:mb-8 flex items-center gap-1 sm:gap-2 border-b border-gray-200 overflow-x-auto no-scrollbar" data-testid="periodicals-tabs">
            {groups.map((tab) => (
              <button key={tab.key} onClick={() => selectGroup(tab.key)} data-testid={`periodicals-tab-${tab.key}`}
                className={`px-4 sm:px-5 py-2.5 sm:py-3 text-sm font-semibold transition-colors -mb-px border-b-2 whitespace-nowrap flex-shrink-0 ${group === tab.key ? 'border-cmic-600 text-cmic-700' : 'border-transparent text-charcoal-500 hover:text-charcoal-700'}`}>
                {tab[`name_${locale}`] || tab.name_es}
              </button>
            ))}
          </div>

          <div className="mb-10 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="periodicals-search-container">
            <div className="flex items-center gap-3 px-5 py-4">
              <Search size={20} className="text-charcoal-400 flex-shrink-0" />
              <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="periodicals-search-input"
                placeholder={locale === 'es' ? 'Buscar palabra clave en publicaciones...' : 'Search keyword in publications...'}
                className="flex-1 outline-none text-charcoal-800 placeholder:text-charcoal-300 text-[15px] bg-transparent" />
              {query && (
                <button onClick={() => { setQuery(''); setResults(null); }} className="p-1.5 rounded-lg text-charcoal-400 hover:bg-gray-100"><X size={16} /></button>
              )}
              {searching ? (
                <Loader2 size={20} className="text-cmic-500 animate-spin" />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-charcoal-700 flex items-center justify-center"><Search size={18} className="text-white" /></div>
              )}
            </div>
            {query && query.trim().length >= 2 && (
              <div className="px-5 pb-3">
                <p className="text-xs text-charcoal-400">
                  {searching ? (locale === 'es' ? 'Buscando...' : 'Searching...')
                    : results && results.length === 0 ? (locale === 'es' ? `Sin resultados para "${query}"` : `No results for "${query}"`)
                    : results ? (locale === 'es' ? `${results.length} publicación(es) con coincidencias` : `${results.length} publication(s) with matches`)
                    : null}
                </p>
              </div>
            )}
          </div>

          {results !== null && results.length > 0 && (
            <div className="mb-10 space-y-4" data-testid="periodicals-search-results">
              {results.map((r) => (
                <div key={r.file_id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0"><FileSearch size={18} className="text-red-500" /></div>
                      <div className="min-w-0">
                        <p className="font-semibold text-charcoal-800 truncate">{r.title}</p>
                        <p className="text-xs text-charcoal-400">{r.total_matches_pages} {locale === 'es' ? 'página(s) con coincidencias' : 'page(s) with matches'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {r.matches.map((m, i) => (
                      <button key={i} onClick={() => openPreview(r.url, r.title, m.page)} data-testid={`search-result-${r.file_id}-page-${m.page}`}
                        className="w-full text-left px-5 py-3.5 hover:bg-cmic-50/40 transition-colors flex items-start gap-3">
                        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-cmic-50 text-cmic-700 text-xs font-bold flex-shrink-0">p.{m.page}</span>
                        <p className="text-sm text-charcoal-600 leading-relaxed flex-1">...{highlight(m.snippet, query.trim())}...</p>
                        <ExternalLink size={14} className="text-charcoal-300 mt-1 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(!results || results.length === 0 || query.trim().length < 2) && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
              {catalog.filter((s) => (s.group || 'flash') === group).length === 0 && (
                <div className="col-span-full bg-white rounded-xl border border-gray-200 p-10 text-center text-charcoal-400 text-sm">
                  {locale === 'es' ? 'Aún no hay publicaciones en esta sección.' : 'No publications in this section yet.'}
                </div>
              )}
              {catalog.filter((s) => (s.group || 'flash') === group)
                .slice()
                .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0))
                .map((section) => (
                <div key={section.id} className={`bg-white rounded-xl border overflow-hidden shadow-sm ${section.featured ? 'border-amber-300 ring-1 ring-amber-200' : 'border-gray-200'}`}>
                  <div className={`px-5 py-3.5 flex items-center justify-between gap-2 ${section.featured ? 'bg-amber-500' : 'bg-cmic-600'}`}>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wider">{section.name[locale]}</h3>
                    {section.featured && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-900 bg-white/90 px-2 py-0.5 rounded-full flex-shrink-0" data-testid={`periodical-featured-badge-${section.id}`}>
                        <Star size={11} className="fill-amber-500 text-amber-500" /> {locale === 'es' ? 'Destacado' : 'Featured'}
                      </span>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100">
                    {section.periods.length === 0 && (
                      <div className="px-5 py-4 text-sm text-charcoal-400 italic">{locale === 'es' ? 'Sin archivos' : 'No files'}</div>
                    )}
                    {section.periods.map((period) => {
                      const open = isPeriodOpen(section.id, period.id);
                      return (
                        <div key={period.id}>
                          <button onClick={() => togglePeriod(section.id, period.id)} data-testid={`periodical-period-${section.id}-${period.id}`}
                            className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors">
                            <span className={`text-sm font-semibold ${open ? 'text-charcoal-800' : 'text-charcoal-600'}`}>{period.label[locale]}</span>
                            {open ? <Minus size={16} className="text-charcoal-400" /> : <Plus size={16} className="text-charcoal-400" />}
                          </button>
                          {open && (
                            <div className="bg-gray-50/60 border-t border-gray-100">
                              {period.files.length === 0 && (
                                <div className="px-5 py-3 text-xs text-charcoal-400 italic">{locale === 'es' ? 'Sin archivos para este periodo' : 'No files for this period'}</div>
                              )}
                              {period.files.map((file) => (
                                <button key={file.id} onClick={() => openPreview(file.url, file.title)} data-testid={`periodical-file-${file.id}`}
                                  className="w-full text-left flex items-center gap-3 px-5 py-3 hover:bg-white transition-colors border-b border-gray-100 last:border-b-0">
                                  <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0"><FileText size={18} className="text-red-500" /></div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-charcoal-700 truncate">{file.title}</p>
                                    <p className="text-xs text-charcoal-400">{formatSize(file.size_bytes)}</p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6" onClick={() => setPreview(null)} data-testid="pdf-preview-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white flex-shrink-0">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0"><FileText size={18} className="text-red-500" /></div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-charcoal-800 text-sm truncate">{preview.title}</h3>
                  <p className="text-xs text-charcoal-400">{locale === 'es' ? 'Vista previa' : 'Preview'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                <a href={preview.url.split('#')[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-xs font-medium text-charcoal-600 hover:text-cmic-600 hover:bg-cmic-50 rounded-lg transition-colors">
                  <ExternalLink size={14} /><span className="hidden sm:inline">{locale === 'es' ? 'Abrir' : 'Open'}</span>
                </a>
                <a href={preview.url.split('#')[0]} download className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-xs font-medium text-charcoal-600 hover:text-cmic-600 hover:bg-cmic-50 rounded-lg transition-colors">
                  <Download size={14} /><span className="hidden sm:inline">{locale === 'es' ? 'Descargar' : 'Download'}</span>
                </a>
                <button onClick={() => setPreview(null)} data-testid="pdf-preview-close" className="p-2 text-charcoal-500 hover:text-charcoal-800 hover:bg-gray-100 rounded-lg transition-colors"><X size={18} /></button>
              </div>
            </div>
            <div className="flex-1 bg-gray-100 min-h-0 relative">
              <object data={preview.url} type="application/pdf" className="w-full h-full">
                <iframe src={preview.url} title={preview.title} className="w-full h-full border-0" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-gray-50">
                  <FileText size={48} className="text-charcoal-300 mb-3" />
                  <p className="text-charcoal-600 mb-4">{locale === 'es' ? 'Tu navegador no puede mostrar PDF aquí.' : 'Your browser cannot display the PDF here.'}</p>
                  <a href={preview.url.split('#')[0]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-cmic-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-cmic-600">
                    <ExternalLink size={16} />{locale === 'es' ? 'Abrir en nueva pestaña' : 'Open in new tab'}
                  </a>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
